import { courseCatalog, courseDetails } from "../data/courseDetails";
import { blogPosts, getBlogBySlug, getBlogPlainText } from "../data/blogPosts";
import { SITE_NAME, SITE_URL, absoluteImageUrl, absoluteUrl } from "./siteConfig";

const FAQ_ITEMS = [
  { question: "How long are the courses?", answer: "The learning paths run from 3 to 6 months depending on the track and project depth." },
  { question: "Do I get hands-on projects?", answer: "Yes. Each course includes practical projects so learners can build a portfolio while learning." },
  { question: "Is the YouTube content free?", answer: "Yes. Demo videos and selected tutorials are available on the YouTube channel." },
  { question: "Who can join?", answer: "Both beginners and working professionals can join, with guided learning paths for each level." },
  { question: "Do I need prior IT experience?", answer: "No. Our beginner-friendly programs are designed for students, fresh graduates, and professionals from any background." },
  { question: "Will I receive a certificate after completion?", answer: "Yes. Learners receive a course completion certificate after successfully completing the training and projects." },
  { question: "Are live classes available?", answer: "Yes. We conduct live interactive sessions where learners can ask questions and get guidance directly from industry engineers." },
  { question: "Do you provide placement assistance?", answer: "Yes. We provide resume building, interview preparation, mock interviews, and career guidance to help learners prepare for job opportunities." },
  { question: "Can I access recorded sessions?", answer: "Yes. Recorded sessions are provided so you can revisit lessons anytime and learn at your own pace." },
  { question: "What technologies will I learn?", answer: "Depending on the course, you may learn AWS, Azure, DevOps tools, Linux, Docker, Kubernetes, Terraform, Git, CI/CD, and Full Stack Development technologies." },
  { question: "How are projects evaluated?", answer: "Projects are reviewed by experienced engineers who provide feedback and guidance to help improve your practical skills." },
  { question: "Can working professionals join the training?", answer: "Absolutely. Our flexible schedules and recorded sessions make it easy for working professionals to learn alongside their jobs." },
];

function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: absoluteImageUrl("/images/c3-logo.png"),
    sameAs: [
      "https://www.youtube.com/@confideocloudcode",
      "https://www.instagram.com/confideoit/",
      "https://www.linkedin.com/company/confideo-it-services/",
    ],
    description:
      "Hands-on cloud, DevOps, and full-stack training with real projects, certification support, and placement assistance.",
  };
}

function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/courses?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

function courseSchema(course) {
  const catalogEntry = courseCatalog.find((item) => item.slug === course.slug);

  return {
    "@context": "https://schema.org",
    "@type": "Course",
    name: course.title,
    description: course.description,
    url: absoluteUrl(`/courses/${course.slug}`),
    image: absoluteImageUrl(course.image),
    provider: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    educationalLevel: "Beginner to Advanced",
    inLanguage: "en",
    ...(catalogEntry?.duration
      ? {
          timeRequired: catalogEntry.duration.replace(/\s+/g, ""),
        }
      : {}),
    offers: {
      "@type": "Offer",
      category: "Paid",
      url: course.formLink,
      availability: "https://schema.org/InStock",
    },
  };
}

function courseListSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${SITE_NAME} Training Courses`,
    itemListElement: courseDetails.map((course, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: absoluteUrl(`/courses/${course.slug}`),
      name: course.title,
    })),
  };
}

function faqPageSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

function blogPostingSchema(post) {
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.metaDescription || post.desc,
    datePublished: post.date,
    dateModified: post.date,
    author: {
      "@type": "Organization",
      name: post.author,
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        "@type": "ImageObject",
        url: absoluteImageUrl("/images/c3-logo.png"),
      },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": absoluteUrl(`/blog/${post.slug}`),
    },
    url: absoluteUrl(`/blog/${post.slug}`),
    image: absoluteImageUrl("/images/c3-logo.png"),
    articleBody: getBlogPlainText(post),
    keywords: (post.keywords || []).join(", "),
    inLanguage: "en-IN",
  };
}

function blogListSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: `${SITE_NAME} Blog`,
    url: absoluteUrl("/blog"),
    description: "Cloud computing, DevOps, AWS, Azure, and career guides from Confideo Cloud & Code.",
    blogPost: blogPosts.map((post) => ({
      "@type": "BlogPosting",
      headline: post.title,
      url: absoluteUrl(`/blog/${post.slug}`),
      datePublished: post.date,
      description: post.metaDescription || post.desc,
    })),
  };
}

function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function getStructuredData(pathname, course = null) {
  const schemas = [organizationSchema()];

  if (pathname === "/") {
    schemas.push(websiteSchema());
  }

  if (pathname === "/courses") {
    schemas.push(courseListSchema());
  }

  if (pathname === "/blog") {
    schemas.push(blogListSchema());
  }

  const blogMatch = pathname.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) {
    const post = getBlogBySlug(blogMatch[1]);
    if (post) {
      schemas.push(blogPostingSchema(post));
      schemas.push(
        breadcrumbSchema([
          { name: "Home", url: absoluteUrl("/") },
          { name: "Blog", url: absoluteUrl("/blog") },
          { name: post.title, url: absoluteUrl(`/blog/${post.slug}`) },
        ])
      );
    }
  }

  if (pathname === "/faq" || pathname === "/") {
    schemas.push(faqPageSchema());
  }

  if (course) {
    schemas.push(courseSchema(course));
  }

  return schemas;
}
